<template>
  <div class="mt-6 p-4">
    <!-- Back button -->
    <BaseButton
      variant="ghost"
      outlined
      class="mb-4"
      @click="$emit('back')"
    >
      <template #icon>
        <svg
          class="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </template>
      Volver al menú
    </BaseButton>

    <!-- Item Options Content -->
    <div class="space-y-4">
      <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">
        {{ item.name }}
      </h3>
      <p
        v-if="item.description"
        class="text-sm text-gray-500 dark:text-gray-400"
      >
        {{ item.description }}
      </p>

      <!-- Variants -->
      <VariantSelector
        :item="item"
        :selected-variant="selectedVariant"
        :get-variant-price="getVariantPrice"
        @select-variant="selectedVariant = $event"
      />

      <!-- Modifier Groups -->
      <ModifierGroups
        :item="item"
        :is-option-selected="isOptionSelected"
        :is-accordion-open="isAccordionOpen"
        :is-group-selection-valid="isGroupSelectionValid"
        @toggle-accordion="toggleAccordion"
        @toggle-option="toggleModifierOption"
      />

      <!-- Item Type Selection (only for dine_in orders) -->
      <ItemTypeSelector
        v-if="orderType === 'Dine-in'"
        :selected-item-type="selectedItemType"
        @select-type="selectedItemType = $event"
      />

      <!-- Quantity Control -->
      <QuantityControl
        :quantity="quantity"
        @decrease="decreaseQuantity"
        @increase="increaseQuantity"
      />

      <!-- Notes -->
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Instrucciones especiales
        </label>
        <textarea
          v-model="notes"
          rows="3"
          class="w-full rounded-lg border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          placeholder="¿Alguna petición especial?"
        />
      </div>

      <!-- Add button -->
      <button
        class="w-full py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="!isModifierSelectionValid"
        @click="handleAdd"
      >
        Agregar • ${{ totalPrice.toFixed(2) }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { ExtendedMenuItem, MenuItemVariant, MenuItemModifierGroupLink } from '@/types/order';
import { useItemOptions } from '@/composables/useItemOptions';
import BaseButton from '@/components/ui/BaseButton.vue';
import VariantSelector from './VariantSelector.vue';
import ModifierGroups from './ModifierGroups.vue';
import ItemTypeSelector from './ItemTypeSelector.vue';
import QuantityControl from './QuantityControl.vue';

interface Props {
  item: ExtendedMenuItem;
  topNotes: string[];
  getVariantPrice: (variant: MenuItemVariant) => number;
  orderType?: 'Dine-in' | 'Takeaway' | 'Delivery';
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'add', data: { item: ExtendedMenuItem; variant: MenuItemVariant | null; quantity: number; notes: string; unit_price: number; item_type: 'dine_in' | 'takeaway' }): void;
}>();

// Use composable for business logic
const {
  selectedVariant,
  notes,
  quantity,
  selectedItemType,
  totalPrice,
  isModifierSelectionValid,
  isGroupSelectionValid,
  isOptionSelected,
  isAccordionOpen,
  toggleAccordion,
  toggleModifierOption,
  decreaseQuantity,
  increaseQuantity,
  buildModifiersSummary,
  reset,
} = useItemOptions({
  item: props.item,
  getVariantPrice: props.getVariantPrice,
});

function handleAdd() {
  if (!isModifierSelectionValid.value) return;

  const modifierNotes = buildModifiersSummary();
  const finalNotes = [modifierNotes, notes.value].filter((part) => part && part.trim().length > 0).join(' | ');
  const unitPrice = totalPrice.value / Math.max(quantity.value, 1);
  
  emit('add', {
    item: props.item,
    variant: selectedVariant.value,
    quantity: quantity.value,
    notes: finalNotes,
    unit_price: unitPrice,
    item_type: selectedItemType.value,
  });
  
  // Reset after adding
  reset();
}
</script>
